import { useState } from "react";
import { Target, Save, User, Users } from "lucide-react";
import { metaDe, META_PADRAO } from "../lib/metas";
import { getToday } from "../utils";

// ═══════════════════════════════════════════════════════════════
// METAS — quem define é a gerência
// ═══════════════════════════════════════════════════════════════
//
// Uma meta geral, que vale para todo mundo, e metas individuais que a
// sobrepõem. O comercial vê a própria meta no painel da Rede; alterar, só
// aqui — meta que o cobrado edita não é meta.

export default function ConfigMetas({ metas = [], setMetas, equipe = [], userName = "" }) {
  const [rascunho, setRascunho] = useState({});
  const [salvo, setSalvo] = useState("");

  const valorDe = (id, campo, padrao) => {
    if (rascunho[id]?.[campo] !== undefined) return rascunho[id][campo];
    const m = metas.find((x) => x.id === id);
    return m?.[campo] ?? padrao;
  };

  const mexer = (id, campo, valor) =>
    setRascunho((p) => ({ ...p, [id]: { ...(p[id] || {}), [campo]: valor } }));

  const salvar = (id, extra = {}) => {
    const linha = {
      id,
      escopo: id === "geral" ? "geral" : "usuario",
      userId: id === "geral" ? null : id,
      contatosDia: Number(valorDe(id, "contatosDia", META_PADRAO.contatosDia)) || 0,
      contatosSemana: Number(valorDe(id, "contatosSemana", META_PADRAO.contatosSemana)) || 0,
      ativa: true,
      atualizadoEm: getToday(),
      atualizadoPor: userName,
      ...extra,
    };
    setMetas((prev) => (prev.some((m) => m.id === id)
      ? prev.map((m) => (m.id === id ? { ...m, ...linha } : m))
      : [...prev, linha]));
    setRascunho((p) => ({ ...p, [id]: undefined }));
    setSalvo(id);
    setTimeout(() => setSalvo(""), 2500);
  };

  const remover = (id) => setMetas((prev) => prev.filter((m) => m.id !== id));

  const geral = metaDe(metas, "___sem_usuario___");

  return (
    <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #e5e7eb", padding: "16px 18px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
        <Target size={16} color="#7c3aed" />
        <span style={{ fontSize: 14, fontWeight: 800, color: "#0f172a" }}>Metas de contato</span>
      </div>
      <div style={{ fontSize: 11.5, color: "#64748b", marginBottom: 14, lineHeight: 1.45 }}>
        Quantos contatos por dia cada comercial precisa registrar. Conta o contato registrado —
        e o painel mostra à parte quantos vieram com print, porque meta que aceita registro sem
        prova ensina a inventar registro.
      </div>

      <Linha
        icone={<Users size={13} color="#7c3aed" />}
        titulo="Meta geral"
        subtitulo="Vale para todo comercial que não tenha meta própria"
        dia={valorDe("geral", "contatosDia", META_PADRAO.contatosDia)}
        semana={valorDe("geral", "contatosSemana", META_PADRAO.contatosSemana)}
        onDia={(v) => mexer("geral", "contatosDia", v)}
        onSemana={(v) => mexer("geral", "contatosSemana", v)}
        onSalvar={() => salvar("geral")}
        salvo={salvo === "geral"}
      />

      <div style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.04em", margin: "16px 0 6px" }}>
        Metas individuais
      </div>
      {equipe.length === 0 && (
        <div style={{ fontSize: 11.5, color: "#94a3b8" }}>Nenhum usuário carregado.</div>
      )}
      {equipe.map((p) => {
        const tem = metas.some((m) => m.id === p.id);
        return (
          <Linha
            key={p.id}
            icone={<User size={13} color="#64748b" />}
            titulo={p.full_name || p.email || p.id}
            subtitulo={tem ? "Meta própria" : `Usando a geral (${geral.contatosDia}/dia)`}
            dia={valorDe(p.id, "contatosDia", geral.contatosDia)}
            semana={valorDe(p.id, "contatosSemana", geral.contatosSemana)}
            onDia={(v) => mexer(p.id, "contatosDia", v)}
            onSemana={(v) => mexer(p.id, "contatosSemana", v)}
            onSalvar={() => salvar(p.id, { nome: p.full_name || p.email })}
            onRemover={tem ? () => remover(p.id) : null}
            salvo={salvo === p.id}
          />
        );
      })}
    </div>
  );
}

function Linha({ icone, titulo, subtitulo, dia, semana, onDia, onSemana, onSalvar, onRemover, salvo }) {
  const num = { width: 62, padding: "6px 8px", borderRadius: 6, border: "1px solid #d1d5db", fontSize: 12, textAlign: "center", color: "#334155", background: "#fff" };
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", padding: "9px 0", borderTop: "1px solid #f1f5f9" }}>
      <span style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 190, flex: 1 }}>
        {icone}
        <span style={{ minWidth: 0 }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{titulo}</div>
          <div style={{ fontSize: 10, color: "#94a3b8" }}>{subtitulo}</div>
        </span>
      </span>
      <label style={{ fontSize: 10.5, color: "#64748b", display: "flex", alignItems: "center", gap: 5 }}>
        dia <input type="number" min="0" value={dia} onChange={(e) => onDia(e.target.value)} style={num} />
      </label>
      <label style={{ fontSize: 10.5, color: "#64748b", display: "flex", alignItems: "center", gap: 5 }}>
        semana <input type="number" min="0" value={semana} onChange={(e) => onSemana(e.target.value)} style={num} />
      </label>
      <button onClick={onSalvar} style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 12px", borderRadius: 7, border: "none", background: salvo ? "#059669" : "#7c3aed", color: "#fff", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
        <Save size={12} /> {salvo ? "Salvo" : "Salvar"}
      </button>
      {onRemover && (
        <button onClick={onRemover} style={{ padding: "6px 10px", borderRadius: 7, border: "1px solid #e2e8f0", background: "#fff", color: "#64748b", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
          Usar a geral
        </button>
      )}
    </div>
  );
}
